import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import {
  AdminOrganizationUser,
  UpdateAdminOrganizationUserPayload
} from '@utils/types/admin';
import { ADMIN_ORGANIZATION_USERS_QUERY_KEY } from './use-create-admin-organization-user';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type UpdateUserParams = {
  organizationId: number | string;
  userId: number | string;
  payload: UpdateAdminOrganizationUserPayload;
};

/**
 * Hook to update a user in an on-prem organization
 *
 * PUT /api/admin/on-prem/organizations/{organization_id}/users/{user_id}
 */
export const useUpdateAdminOrganizationUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateUser = async ({
    organizationId,
    userId,
    payload
  }: UpdateUserParams) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/users/${userId}`;
    const response = await Axios.put<BaseResponse<AdminOrganizationUser>>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    BaseResponse<AdminOrganizationUser>,
    AxiosError<BaseError>,
    UpdateUserParams
  >(updateUser, {
    onSuccess: (_, variables) => {
      toast.success(t('user-updated-successfully'));
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        variables.organizationId
      ]);
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        String(variables.organizationId)
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-update-user');
      toast.error(String(errorMessage));
    }
  });
};
